import { shippingConstants } from "@/apiUtils/constants";
import { deleteCookieClient, setCookieClient } from "@/apiUtils/cookieClientUtils";
import { getLocalStorage, removeLocalStorage, setLocalStorage } from "@/apiUtils/localStorageUtils";
import { Checkout } from "@/common/lib/types";

export const CHECKOUT_RESUME_STORAGE_KEY = "doctormCheckoutResume";
const RESUME_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export type CheckoutResumeState = {
  checkoutId?: string;
  paymentMethod?: string;
  /** Set while the shopper walks back to shipping from payment, so shipping is not skipped. */
  editingShipping?: boolean;
  updatedAt?: number;
};

function persistResumeCookie(paymentMethod?: string): void {
  if (typeof document === "undefined") {
    return;
  }
  if (paymentMethod) {
    const expires = new Date(Date.now() + RESUME_MAX_AGE_MS);
    setCookieClient(CHECKOUT_RESUME_STORAGE_KEY, paymentMethod, { path: "/", expires });
    return;
  }
  deleteCookieClient(CHECKOUT_RESUME_STORAGE_KEY);
}

/**
 * Optional checkout session hints. Cart and guest login always open shipping.
 */
export function setCheckoutResume(state: CheckoutResumeState): void {
  const previous = getCheckoutResume();
  const next = {
    checkoutId: state.checkoutId ?? previous?.checkoutId,
    paymentMethod: state.paymentMethod ?? previous?.paymentMethod,
    editingShipping: state.editingShipping ?? previous?.editingShipping,
    updatedAt: Date.now(),
  };
  setLocalStorage(CHECKOUT_RESUME_STORAGE_KEY, next);
  persistResumeCookie(next.paymentMethod);
}

export function getCheckoutResume(): CheckoutResumeState | undefined {
  const state = getLocalStorage<CheckoutResumeState>(CHECKOUT_RESUME_STORAGE_KEY);
  if (!state) {
    return undefined;
  }
  if (state.updatedAt && Date.now() - state.updatedAt > RESUME_MAX_AGE_MS) {
    removeLocalStorage(CHECKOUT_RESUME_STORAGE_KEY);
    persistResumeCookie(undefined);
    return undefined;
  }
  return state;
}

export function hasPaymentMethodResume(): boolean {
  return Boolean(getCheckoutResume()?.paymentMethod);
}

export function clearCheckoutResume(): void {
  removeLocalStorage(CHECKOUT_RESUME_STORAGE_KEY);
  persistResumeCookie(undefined);
}

/**
 * Called when the shopper leaves payment to edit shipping. The payment method is kept so the
 * summary still shows its discount and payment reopens pre-selected; only the automatic
 * shipping → payment redirect is suppressed for that one visit.
 */
export function markShippingEdit(): void {
  if (!hasPaymentMethodResume()) {
    return;
  }
  setCheckoutResume({ editingShipping: true });
}

export function isShippingEditInProgress(): boolean {
  return Boolean(getCheckoutResume()?.editingShipping);
}

/** Cleared once payment is reached again, so the edit survives a refresh on shipping. */
export function clearShippingEdit(): void {
  if (!isShippingEditInProgress()) {
    return;
  }
  setCheckoutResume({ editingShipping: false });
}

/**
 * Cart / guest login always start checkout on shipping.
 */
export function getCheckoutResumePath(checkout?: Checkout | null): string {
  const isCnc =
    checkout?.deliveryMethod?.__typename === shippingConstants.SHIPPING_TYPE.WARE_HOUSE;

  if (isCnc) {
    return `/checkout/shipping?${shippingConstants.KEY_METHOD}=${shippingConstants.CNC}`;
  }

  return "/checkout/shipping";
}
